import React, { useState } from 'react';
import type { FC, FormEventHandler } from 'react';

import { Alert } from '@shared/components/bootstrap/alert';
import { Button, NavLinkButton } from '@shared/components/bootstrap/button';
import { Panel } from '@shared/components/bootstrap/panel';

import { InputFormGroup } from '@admin/components/fields/input_form_group';
import { StateSelectFormGroup } from '@admin/components/fields/state_select_form_group';
import { TextAreaFormGroup } from '@admin/components/fields/text_area_form_group';
import { RegionSelectFormGroup } from '@admin/components/regions/select_form_group';
import { VehicleTypeSelectFormGroup } from '@admin/components/vehicle_types/select_form_group';
import { WarehouseSelectFormGroup } from '@admin/components/warehouses/select_form_group';
import { vehiclesURL } from '@admin/config/routes';
import { Vehicle__Status } from '@admin/schema';
import type { Vehicle__Input } from '@admin/schema';

import { OwnershipKindSelectFormGroup } from './ownership_kind_select_form_group';
import { VehicleStatusSelectFormGroup } from './status_select_form_group';

import './vehicle_form.scss';

export const VehicleForm: FC<{
  error?: string | null;
  loading?: boolean;
  defaults?: Partial<Vehicle__Input>;
  canEditStatus?: boolean;
  canEditDetails?: boolean;
  save(_: Vehicle__Input): void;
}> = ({ defaults, error, loading, save, canEditStatus = true, canEditDetails = true }) => {
  const [serialCode, setSerialCode] = useState<string>(defaults?.serialCode ?? '');
  const [comments, setComments] = useState<string>(defaults?.comments ?? '');
  const [vin, setVin] = useState<string>(defaults?.vin ?? '');
  const [licensePlate, setLicensePlate] = useState<string>(defaults?.licensePlate ?? '');
  const [tollTagNumber, setTollTagNumber] = useState<string>(defaults?.tollTagNumber ?? '');
  const [grossWeightRating, setGrossWeightRating] = useState<string>(String(defaults?.grossWeightRating ?? ''));
  const [originState, setOriginState] = useState<string | undefined>(defaults?.originState ?? undefined);
  const [registrationExpiresOn, setRegistrationExpiresOn] = useState<string>(defaults?.registrationExpiresOn ?? '');
  const [inspectionExpiresOn, setInspectionExpiresOn] = useState<string>(defaults?.inspectionExpiresOn ?? '');
  const [maintenanceDueOn, setMaintenanceDueOn] = useState<string>(defaults?.maintenanceDueOn ?? '');
  const [providerUnitNumber, setProviderUnitNumber] = useState<string>(defaults?.providerUnitNumber ?? '');
  const [irmNumber, setIrmNumber] = useState<string>(defaults?.irmNumber ?? '');
  const [overwrittenNumSeats, setOverwrittenNumSeats] = useState<string>(String(defaults?.overwrittenNumSeats ?? ''));
  const [overwrittenCuft, setOverwrittenCuft] = useState<string>(String(defaults?.overwrittenCuft ?? ''));
  const [warehouseID, setWarehouseID] = useState<string | undefined>(defaults?.warehouseID);
  const [regionID, setRegionID] = useState<string | undefined>(defaults?.regionID);
  const [status, setStatus] = useState(defaults?.status);
  const [statusChangeReason, setStatusChangeReason] = useState('');
  const [statusChangeReasonError, setStatusChangeReasonError] = useState<string | null>(null);
  const [ownershipKind, setOwnershipKind] = useState(defaults?.ownershipKind ?? undefined);
  const [typeID, setTypeID] = useState<string | undefined>(defaults?.typeID);

  const detailsDisabled = !canEditDetails;
  const statusDisabled = !canEditStatus;
  const statusOnlyEditor = canEditStatus && !canEditDetails;

  const statusIsChanging = status !== defaults?.status;
  const showStatusChangeNote = canEditStatus && statusIsChanging;
  const requiresStatusChangeReason = showStatusChangeNote && !!status && status !== Vehicle__Status.Active;

  const onSubmit: FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (!warehouseID) return;
    if (!regionID) return;
    if (!typeID) return;
    if (!status) return;

    if (requiresStatusChangeReason && !statusChangeReason.trim()) {
      setStatusChangeReasonError('Enter a reason when setting status to something other than Active.');
      return;
    }
    setStatusChangeReasonError(null);

    const detailInput: Omit<Vehicle__Input, 'status' | 'statusChangeReason'> = canEditDetails
      ? {
          comments,
          grossWeightRating: grossWeightRating ? Number(grossWeightRating) : undefined,
          inspectionExpiresOn: inspectionExpiresOn || undefined,
          irmNumber,
          licensePlate,
          maintenanceDueOn: maintenanceDueOn || undefined,
          originState,
          overwrittenCuft: overwrittenCuft ? Number(overwrittenCuft) : undefined,
          overwrittenNumSeats: overwrittenNumSeats ? Number(overwrittenNumSeats) : undefined,
          ownershipKind,
          providerUnitNumber,
          regionID,
          registrationExpiresOn: registrationExpiresOn || undefined,
          serialCode,
          tollTagNumber,
          typeID,
          vin,
          warehouseID,
        }
      : {
          comments: defaults?.comments ?? '',
          grossWeightRating: defaults?.grossWeightRating,
          inspectionExpiresOn: defaults?.inspectionExpiresOn,
          irmNumber: defaults?.irmNumber ?? '',
          licensePlate: defaults?.licensePlate ?? '',
          maintenanceDueOn: defaults?.maintenanceDueOn,
          originState: defaults?.originState,
          overwrittenCuft: defaults?.overwrittenCuft,
          overwrittenNumSeats: defaults?.overwrittenNumSeats,
          ownershipKind: defaults?.ownershipKind,
          providerUnitNumber: defaults?.providerUnitNumber ?? '',
          regionID: defaults?.regionID ?? regionID,
          registrationExpiresOn: defaults?.registrationExpiresOn,
          serialCode: defaults?.serialCode ?? '',
          tollTagNumber: defaults?.tollTagNumber ?? '',
          typeID: defaults?.typeID ?? typeID,
          vin: defaults?.vin ?? '',
          warehouseID: defaults?.warehouseID ?? warehouseID,
        };

    save({
      ...detailInput,
      status: canEditStatus ? status : (defaults?.status as Vehicle__Input['status']),
      statusChangeReason: showStatusChangeNote && statusChangeReason.trim() ? statusChangeReason.trim() : undefined,
    });
  };

  return (
    <form onSubmit={onSubmit}>
      <Panel>
        <Panel.Header>
          <Panel.Title>Details</Panel.Title>
        </Panel.Header>
        <Panel.Body className={detailsDisabled ? 'vehicle-form--details-read-only' : undefined}>
          {error && <Alert style="danger">{error}</Alert>}
          {statusOnlyEditor && (
            <Alert style="info">
              You can change status on this vehicle only. Add a status note below; it is appended to comment history.
              Other fields are read-only.
            </Alert>
          )}

          <InputFormGroup
            id="vehicle_serial_code"
            type="text"
            label="Serial Code:"
            value={serialCode}
            disabled={detailsDisabled}
            onChange={(event) => setSerialCode(event.target.value)}
            placeholder="(Region)(Type)-(Number) - eg. 1A-0051 or 1An-0004"
          />

          <WarehouseSelectFormGroup
            required
            id="vehicle_warehouse_id"
            label="Warehouse:"
            value={warehouseID}
            disabled={detailsDisabled}
            onChange={setWarehouseID}
          />

          <RegionSelectFormGroup
            required
            id="vehicle_region_id"
            label="Region:"
            value={regionID}
            disabled={detailsDisabled}
            onChange={setRegionID}
          />

          <VehicleStatusSelectFormGroup
            required
            id="vehicle_status"
            label="Status:"
            value={status}
            disabled={statusDisabled}
            onChange={(value) => {
              setStatus(value);
              setStatusChangeReasonError(null);
            }}
          />

          {!canEditDetails && (
            <TextAreaFormGroup
              id="vehicle_comment_history"
              label="Comment history:"
              value={comments}
              disabled
              readOnly
              onChange={() => undefined}
            />
          )}

          {showStatusChangeNote && (
            <TextAreaFormGroup
              required={requiresStatusChangeReason}
              id="vehicle_status_change_reason"
              label={requiresStatusChangeReason ? 'Reason for status change:' : 'Note for status change (optional):'}
              value={statusChangeReason}
              disabled={statusDisabled}
              onChange={(event) => {
                setStatusChangeReason(event.target.value);
                setStatusChangeReasonError(null);
              }}
              placeholder={
                requiresStatusChangeReason
                  ? 'Why is this vehicle being set to a non-Active status?'
                  : 'Optional note about this status change (appended to comment history).'
              }
            />
          )}
          {statusChangeReasonError && <Alert style="danger">{statusChangeReasonError}</Alert>}

          <OwnershipKindSelectFormGroup
            id="vehicle_ownership_kind"
            label="Ownership:"
            value={ownershipKind}
            disabled={detailsDisabled}
            onChange={setOwnershipKind}
          />

          <InputFormGroup
            id="vehicle_overwritten_num_seats"
            type="number"
            step={1}
            min={1}
            max={6}
            label="Num Seats:"
            value={overwrittenNumSeats}
            disabled={detailsDisabled}
            onChange={(event) => setOverwrittenNumSeats(event.target.value)}
          />

          <InputFormGroup
            id="vehicle_overwritten_cuft"
            type="number"
            label="CUFT:"
            value={overwrittenCuft}
            disabled={detailsDisabled}
            onChange={(event) => setOverwrittenCuft(event.target.value)}
          />

          <VehicleTypeSelectFormGroup
            required
            id="vehicle_type_id"
            label="Type:"
            value={typeID}
            disabled={detailsDisabled}
            onChange={setTypeID}
          />

          <InputFormGroup
            id="vehicle_irm_number"
            type="text"
            label="IRM #:"
            value={irmNumber}
            disabled={detailsDisabled}
            onChange={(event) => setIrmNumber(event.target.value)}
          />

          <InputFormGroup
            id="vehicle_provider_unit_number"
            type="text"
            label="Provider Unit #:"
            value={providerUnitNumber}
            disabled={detailsDisabled}
            onChange={(event) => setProviderUnitNumber(event.target.value)}
          />

          <InputFormGroup
            id="vehicle_toll_tag_number"
            type="text"
            label="EZ Pass / Toll Tag #:"
            value={tollTagNumber}
            disabled={detailsDisabled}
            onChange={(event) => setTollTagNumber(event.target.value)}
          />

          <InputFormGroup
            id="vehicle_vin"
            type="text"
            label="VIN:"
            value={vin}
            disabled={detailsDisabled}
            onChange={(event) => setVin(event.target.value)}
          />

          <InputFormGroup
            id="vehicle_license_plate"
            label="License Plate:"
            value={licensePlate}
            disabled={detailsDisabled}
            onChange={(event) => setLicensePlate(event.target.value)}
          />

          <InputFormGroup
            id="vehicle_gross_weight_rating"
            label="GWR (Gross Weight Rating) in Pounds:"
            value={grossWeightRating}
            disabled={detailsDisabled}
            onChange={(event) => setGrossWeightRating(event.target.value)}
          />

          <StateSelectFormGroup
            id="vehicle_origin_state"
            label="Origin State/Province:"
            value={originState}
            disabled={detailsDisabled}
            onChange={(value) => setOriginState(value)}
          />

          <InputFormGroup
            id="vehicle_registration_expires_on"
            type="date"
            label="Registration Expiration Date:"
            value={registrationExpiresOn}
            disabled={detailsDisabled}
            onChange={(event) => {
              setRegistrationExpiresOn(event.target.value);
            }}
          />

          <InputFormGroup
            id="vehicle_inspection_expires_on"
            type="date"
            label="Inspection Expiration Date:"
            value={inspectionExpiresOn}
            disabled={detailsDisabled}
            onChange={(event) => {
              setInspectionExpiresOn(event.target.value);
            }}
          />

          <InputFormGroup
            id="vehicle_maintenance_due_on"
            type="date"
            label="Preventative Maintenance Expiration Date:"
            value={maintenanceDueOn}
            disabled={detailsDisabled}
            onChange={(event) => {
              setMaintenanceDueOn(event.target.value);
            }}
          />

          {canEditDetails && (
            <TextAreaFormGroup
              id="vehicle_comments"
              label="Comments:"
              value={comments}
              onChange={(event) => {
                setComments(event.target.value);
              }}
            />
          )}
        </Panel.Body>

        <Panel.Footer align="right">
          <NavLinkButton kind="default" to={vehiclesURL()} disabled={loading}>
            Cancel
          </NavLinkButton>
          <Button kind="primary" type="submit" loading={loading}>
            Save
          </Button>
        </Panel.Footer>
      </Panel>
    </form>
  );
};
